import { useState } from "react";
import { Alert, Button, Card, CardContent, CardHeader, Grid, Snackbar, Tooltip, Typography } from "@mui/material"
import AddIcon from '@mui/icons-material/Add';

import { ReactHookForm } from "../../../components/hook-form/react-hook-form.component"
import { FormAutoCompleteSelectDynamic } from "../../../components/hook-form/form-autocomplete-select.dynamic.component"
import { officeApiKeyStore } from "../../../api/office-api-key-store"
import { projectsApiKeyStore } from "../../../api/projects-api-key-store"
import { unitsApiKeyStore } from "../../../api/units-api-key-store"
import { useReactHookForm } from "../../../hooks/useReactHookForm";
import { useFormContext } from "react-hook-form";

const styling = {
    labelStyle: { paddingBottom: 5, fontWeight: 'bold', fontSize: 16 },
    moveCardStyle: {
        backgroundColor: '#EBEDEF',
        paddingBottom: 15,
        border: '1px solid #D5D8DC',
        borderRadius: 10
    },
};

interface MovePlanForm {
    customerID: number;
    onOfficeChange: (data: any) => void;
}

const MovePlanForm = ({ customerID, onOfficeChange }: MovePlanForm) => {
    const { setValue } = useFormContext();

    const handleOfficeChange = (value: any) => {
        setValue('project', null)
        setValue('unit', null)
        onOfficeChange(value);
    }

    const handleProjectChange = (value: any) => {
        setValue('unit', null)
    }

    return (
        <>
            <Grid item xl={3.5} lg={3.5} sm={3.5} xs={3.5}>
                <FormAutoCompleteSelectDynamic
                    name="office"
                    label="Office"
                    onChange={handleOfficeChange}
                    getQueryKeyConfig={() => officeApiKeyStore.getCustomerOffices(customerID ?? 0)}
                    optionsValueKey="officeID"
                    optionsLabelKey="officeName"
                />
            </Grid>
            <Grid item xl={3.5} lg={3.5} sm={3.5} xs={3.5}>
                <FormAutoCompleteSelectDynamic
                    name="project"
                    label="Project"
                    onChange={handleProjectChange}
                    watchKeys={['office']}
                    getQueryKeyConfig={(value: any) => projectsApiKeyStore.getOfficeProjects(value.office?.officeID ?? 0)}
                    optionsValueKey="projectID"
                    optionsLabelKey="projectName"
                />
            </Grid>
            <Grid item xl={3.5} lg={3.5} sm={3.5} xs={3.5}>
                <FormAutoCompleteSelectDynamic
                    name="unit"
                    label="Unit"
                    watchKeys={['project']}
                    getQueryKeyConfig={(value: any) => unitsApiKeyStore.getProjectUnits(value.project?.projectID ?? 0)}
                    optionsValueKey="unitID"
                    optionsLabelKey="unitName"
                />
            </Grid>
        </>
    );
}

interface MovePlanComponent {
    customerID: number;
    handleNewMappedData: (data: any) => void;
}

export const MovePlanComponent = ({ customerID, handleNewMappedData }: MovePlanComponent) => {
    const form = useReactHookForm({
        defaultValues: {
            office: { officeID: '', officeName: '' },
            project: { projectID: '', projectName: '' },
            unit: { unitID: '', unitName: '' },
            isMapOffice: false,
            isMapProject: false,
            isMapUnit: false
        }
    });
    const [isOpen, setIsOpen] = useState(false);
    const [disableOffice, setDisableOffice] = useState<boolean>(true);
    const [message, setMessage] = useState({ severity: 'success', messageInfo: '' });

    const handleSubmit = async (movingData: any) => {
        const mappingData:any = {
            planOfficeMappings: [
                {
                    officeCode: movingData?.office?.officeCode,
                    officeName: movingData?.office?.officeName,
                    officeID: movingData?.office?.officeID,
                    isAdded: true,
                    officeCheckbox: true,
                    projectCount: 1,
                    unitCount: 1,
                    planProjectMappings: movingData?.project?.projectID ? [
                        {
                            projectName: movingData?.project?.projectName,
                            projectCode: movingData?.project?.projectCode,
                            projectID: movingData?.project?.projectID,
                            isAdded: true,
                            projectCheckbox: true,
                            unitCount: 1,
                            planUnitMappings: movingData?.unit?.unitID ? [
                                {
                                    unitName: movingData?.unit?.unitName,
                                    unitNumber: movingData?.unit?.unitNumber,
                                    unitID: movingData?.unit?.unitID,
                                    isAdded: true,
                                    unitCheckbox: true
                                }
                            ] : []
                        }
                    ] : []
                }
            ]
        }
        handleNewMappedData(mappingData);
        setDisableOffice(true);
        form.reset({
            office: { officeID: '', officeName: '' },
            project: { projectID: '', projectName: '' },
            unit: { unitID: '', unitName: '' },
        })
    };

    const handleOfficeChange = (value: any) => {
        setDisableOffice(!value ? true : false)
    }

    const handleSubmitClick = () => {
        form.handleSubmit(handleSubmit)();
    };

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setMessage({ severity: 'success', messageInfo: '' });
        setIsOpen(false);
    };

    return (
        <ReactHookForm formName="ADD_MODULES_PLAN_FORM" onSubmit={handleSubmit} formContextProps={form}>
            <Card elevation={0}>
                <CardHeader style={{ padding: 0 }}
                    title={
                        <Typography variant="h6" style={styling.labelStyle}>
                            Map to Office / Project / Unit
                        </Typography>
                    }
                />
                <CardContent style={styling.moveCardStyle}>
                    <Grid container spacing={2}>
                        <MovePlanForm customerID={customerID} onOfficeChange={handleOfficeChange} />
                        <Grid item xl={1} lg={1} sm={1} xs={1}>
                            {
                                disableOffice ?
                                    <Button
                                        fullWidth
                                        type="button"
                                        variant="contained"
                                        disabled={disableOffice}
                                        style={{ height: 38, maxWidth: 38 }}>
                                        <AddIcon />
                                    </Button>
                                    :
                                    <Tooltip title={<Typography variant="body1">Map</Typography>}>
                                        <Button
                                            fullWidth
                                            type="button"
                                            variant="contained"
                                            onClick={handleSubmitClick}
                                            style={{ height: 38, maxWidth: 38 }}>
                                            <AddIcon />
                                        </Button>
                                    </Tooltip>
                            }
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <Snackbar
                open={isOpen}
                autoHideDuration={6000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                onClose={handleClose}
            >
                <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={handleClose}>
                    {!message.messageInfo ? ' ' : message?.messageInfo}
                </Alert>
            </Snackbar>
        </ReactHookForm>
    )
}